import { ToolState, ToolType, useToolState } from "../AccountDetails";
import {
  useIsAgentCMInfo,
  useIsAgentLostStolen,
  useIsAgentOnlineStatements,
  useIsAgentPhones,
  useIsAgentRoleACH,
  useIsAgentRoleCreditLineIncrease,
  useIsAgentRoleCycleToDateTransactions,
  useIsAgentRoleMakePayment,
  useIsAgentRolePermissionToDiscuss,
  useIsAgentRoleStatementHistory,
  useIsAgentRoleTerms,
  useIsAgentRoleUsername,
} from "../../lib/roleContext";
import ActiveToolButton from "./ActiveToolButton";
import CloseToolConfirmationModal from "../AccountDetails/CloseToolConfirmationModal/CloseToolConfirmationModal";
import { Channel, GetAccountResponse } from "../../lib/api/models";
import SelectToolButton from "./SelectToolButton";
import ToolbarExpansionButton from "./ToolbarExpansionButton";
import ToolbarSummaryButton from "./ToolbarSummaryButton";
import autopayActive from "../../assets/autopay-active.svg";
import autopayInactive from "../../assets/autopay-inactive.svg";
import changeUsernameActive from "../../assets/change-username-active.svg";
import changeUsernameInactive from "../../assets/change-username-inactive.svg";
import classNames from "classnames";
import creditLineIncreaseActive from "../../assets/credit-line-increase-active.svg";
import creditLineIncreaseInactive from "../../assets/credit-line-increase-inactive.svg";
import cycleToDateTransactionsActive from "../../assets/cycle-to-date-transactions-active.svg";
import cycleToDateTransactionsInactive from "../../assets/cycle-to-date-transactions-inactive.svg";
import lostStolenActive from "../../assets/lost-stolen-active.svg";
import lostStolenInactive from "../../assets/lost-stolen-inactive.svg";
import makePaymentActive from "../../assets/make-payment-active.svg";
import makePaymentInactive from "../../assets/make-payment-inactive.svg";
import notesActive from "../../assets/notes-active.svg";
import notesInactive from "../../assets/notes-inactive.svg";
import notificationActive from "../../assets/notification-active.svg";
import notificationInactive from "../../assets/notification-inactive.svg";
import paymentHistoryActive from "../../assets/payment-history-active.svg";
import paymentHistoryInactive from "../../assets/payment-history-inactive.svg";
import paymentTransactionsActive from "../../assets/payment-transactions-active.svg";
import paymentTransactionsInactive from "../../assets/payment-transactions-inactive.svg";
import permissionToDiscussActive from "../../assets/permission-to-discuss-active.svg";
import permissionToDiscussInactive from "../../assets/permission-to-discuss-inactive.svg";
import phonesActive from "../../assets/phones-active.svg";
import phonesInactive from "../../assets/phones-inactive.svg";
import sourceAccountsActive from "../../assets/payment-methods-active.svg";
import sourceAccountsInactive from "../../assets/payment-methods-inactive.svg";
import statementHistoryActive from "../../assets/statement-history-active.svg";
import statementHistoryInactive from "../../assets/statement-history-inactive.svg";
import onlineStatementsActive from "../../assets/online-statements-active.svg";
import onlineStatementsInactive from "../../assets/online-statements-inactive.svg";
import termsActive from "../../assets/terms-active.svg";
import termsInactive from "../../assets/terms-inactive.svg";
import cmInfoActive from "../../assets/cm-info-active.svg";
import cmInfoInactive from "../../assets/cm-info-inactive.svg";
import paperlessServiceActive from "../../assets/paperless-service-active.svg";
import paperlessServiceInactive from "../../assets/paperless-service-inactive.svg";
import cardActivationActive from "../../assets/card-activation-active.svg";
import cardActivationInactive from "../../assets/card-activation-inactive.svg";
import textBankingActive from "../../assets/text-banking-active.svg";
import textBankingInactive from "../../assets/text-banking-inactive.svg";
import resultsActive from "../../assets/results-active.svg";
import resultsInactive from "../../assets/results-inactive.svg";
import debtDisclosureActive from "../../assets/debt-disclosure-active.svg";
import debtDisclosureInactive from "../../assets/debt-disclosure-inactive.svg";
import styles from "./Toolbar.module.css";
import { toolTypeToName } from "../AccountDetails/utils";
import { useState } from "react";
import DebtDisclosureOnCloseWarningModal from "../DebtDisclosureOnCloseWarningModal";
import WarningModal from "../common/modals/WarningModal/WarningModal";
import { ChannelMapToFull } from "../../lib/channelUtil";
import { useChannelState } from "../../lib/channelContext";

const strings = {
  resultModalLabel: (c: Channel) =>
    `Result Required for ${ChannelMapToFull[c]} Channel Modal`,
  resultMustComplete: (c: Channel) =>
    `Result tool must be completed prior to leaving this page or starting a New Search when on the ${ChannelMapToFull[c]} Channel.`,
  resultButtonText: "Go to Results",
};

const toolData: Record<
  ToolType,
  {
    active: string;
    inactive: string;
  }
> = {
  [ToolType.ChangeUsername]: {
    active: changeUsernameActive,
    inactive: changeUsernameInactive,
  },
  [ToolType.TestPushNotification]: {
    active: notificationActive,
    inactive: notificationInactive,
  },
  [ToolType.PaymentTransactions]: {
    active: paymentTransactionsActive,
    inactive: paymentTransactionsInactive,
  },
  [ToolType.PaymentHistory]: {
    active: paymentHistoryActive,
    inactive: paymentHistoryInactive,
  },
  [ToolType.ManageAutopay]: {
    active: autopayActive,
    inactive: autopayInactive,
  },
  [ToolType.MakePayment]: {
    active: makePaymentActive,
    inactive: makePaymentInactive,
  },
  [ToolType.SourceAccounts]: {
    active: sourceAccountsActive,
    inactive: sourceAccountsInactive,
  },
  [ToolType.PermissionToDiscuss]: {
    active: permissionToDiscussActive,
    inactive: permissionToDiscussInactive,
  },
  [ToolType.CycleToDateTransactions]: {
    active: cycleToDateTransactionsActive,
    inactive: cycleToDateTransactionsInactive,
  },
  [ToolType.StatementHistory]: {
    active: statementHistoryActive,
    inactive: statementHistoryInactive,
  },
  [ToolType.LostStolen]: {
    active: lostStolenActive,
    inactive: lostStolenInactive,
  },
  [ToolType.CreditLineIncrease]: {
    active: creditLineIncreaseActive,
    inactive: creditLineIncreaseInactive,
  },
  [ToolType.Terms]: {
    active: termsActive,
    inactive: termsInactive,
  },
  [ToolType.Phones]: {
    active: phonesActive,
    inactive: phonesInactive,
  },
  [ToolType.CMInfo]: {
    active: cmInfoActive,
    inactive: cmInfoInactive,
  },
  [ToolType.OnlineStatements]: {
    active: onlineStatementsActive,
    inactive: onlineStatementsInactive,
  },
  [ToolType.PaperlessService]: {
    active: paperlessServiceActive,
    inactive: paperlessServiceInactive,
  },
  [ToolType.CardActivation]: {
    active: cardActivationActive,
    inactive: cardActivationInactive,
  },
  [ToolType.TextBanking]: {
    active: textBankingActive,
    inactive: textBankingInactive,
  },
  [ToolType.Results]: {
    active: resultsActive,
    inactive: resultsInactive,
  },
  [ToolType.DebtDisclosure]: {
    active: debtDisclosureActive,
    inactive: debtDisclosureInactive,
  },
  [ToolType.Notes]: {
    active: notesActive,
    inactive: notesInactive,
  },
};

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

type Props = {
  account: GetAccountResponse;
  toolState: ReturnType<typeof useToolState>;
  scrollToCompleted: () => void;
  isDebtDisclosureLoaded: boolean;
  isDebtDisclosureComplete: boolean;
  isResultRequired: boolean;
  isResultComplete: boolean;
};

export function Toolbar({
  account,
  toolState,
  scrollToCompleted,
  isDebtDisclosureLoaded,
  isDebtDisclosureComplete,
  isResultRequired,
  isResultComplete,
}: Props) {
  const { channel } = useChannelState();

  const [showDebtDisclosureWarningModal, setShowDebtDisclosureWarningModal] =
    useState(false);

  const [showResultRequiredModal, setShowResultRequiredModal] = useState(false);

  const [isToolbarExpanded, setIsToolbarExpanded] = useState(false);
  const [closeConfirmationTool, setCloseConfirmationTool] =
    useState<ToolState>();

  const onExpandOrCollapse = () => {
    setIsToolbarExpanded((prev) => !prev);
  };

  const onCloseToolbar = () => {
    setIsToolbarExpanded(false);
  };

  const onClickCloseTool = (tool: ToolState) => {
    if (
      tool.type === ToolType.DebtDisclosure &&
      isDebtDisclosureLoaded &&
      (!isDebtDisclosureComplete || tool.inProgress)
    ) {
      setShowDebtDisclosureWarningModal(true);
      return;
    }

    if (
      tool.type === ToolType.Results &&
      isResultRequired &&
      !isResultComplete
    ) {
      setShowResultRequiredModal(true);
      return;
    }

    if (tool.inProgress && !closeConfirmationTool) {
      setCloseConfirmationTool(tool);
      return;
    }

    toolState.onRequestCloseTool(tool.id);
    onCloseToolbar();
  };

  const canMakePayment = useIsAgentRoleMakePayment();
  const canChangeUsername = useIsAgentRoleUsername();
  const canManageAutopay = useIsAgentRoleACH();
  const canP2D = useIsAgentRolePermissionToDiscuss();
  const canC2D = useIsAgentRoleCycleToDateTransactions();
  const canViewStatementHistory = useIsAgentRoleStatementHistory();
  const canViewCreditLineIncrease = useIsAgentRoleCreditLineIncrease();
  const canViewTerms = useIsAgentRoleTerms();
  const canViewPhones = useIsAgentPhones();
  const canViewCMInfo = useIsAgentCMInfo();
  const canViewLostStolen = useIsAgentLostStolen();
  const canViewOnlineStatements = useIsAgentOnlineStatements();

  const onSelectTool = (type: ToolType) => {
    if (
      type !== ToolType.DebtDisclosure &&
      isDebtDisclosureLoaded &&
      !!toolState.activeTools.find(
        (tool) => tool.type === ToolType.DebtDisclosure,
      ) &&
      !isDebtDisclosureComplete
    ) {
      setShowDebtDisclosureWarningModal(true);
      return;
    }

    toolState.onAddTool({
      type,
      inProgress: false,
    });
    onExpandOrCollapse();
    scrollToTop();
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={classNames(
          styles.container,
          isToolbarExpanded ? styles.expandedContainer : null,
        )}
      >
        <div className={styles.subContainer}>
          <ToolbarExpansionButton
            isExpanded={isToolbarExpanded}
            onClick={onExpandOrCollapse}
            className={styles.toolbarExpansionButton}
          />
          <div className={styles.activeToolsContainer}>
            {toolState.activeTools.map((tool) => (
              <ActiveToolButton
                key={tool.id}
                className={styles.activeTool}
                icon={toolData[tool.type].active}
                isExpanded={isToolbarExpanded}
                label={toolTypeToName(tool.type)}
                onClickCloseTool={() => onClickCloseTool(tool)}
                onClickActiveTool={() =>
                  document
                    .getElementById(tool.id)
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                inProgress={tool.inProgress}
              />
            ))}
          </div>
          {isToolbarExpanded && (
            <div className={styles.selectToolButtonContainer}>
              {isDebtDisclosureLoaded ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={debtDisclosureInactive}
                  activeIcon={debtDisclosureActive}
                  label={toolTypeToName(ToolType.DebtDisclosure)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.DebtDisclosure,
                  )}
                  onClick={() => onSelectTool(ToolType.DebtDisclosure)}
                />
              ) : null}
              {canViewCMInfo ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={cmInfoInactive}
                  activeIcon={cmInfoActive}
                  label={toolTypeToName(ToolType.CMInfo)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.CMInfo,
                  )}
                  onClick={() => onSelectTool(ToolType.CMInfo)}
                />
              ) : null}
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={paymentHistoryInactive}
                activeIcon={paymentHistoryActive}
                label={toolTypeToName(ToolType.PaymentHistory)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.PaymentHistory,
                )}
                onClick={() => onSelectTool(ToolType.PaymentHistory)}
              />
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={paymentTransactionsInactive}
                activeIcon={paymentTransactionsActive}
                label={toolTypeToName(ToolType.PaymentTransactions)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.PaymentTransactions,
                )}
                onClick={() => onSelectTool(ToolType.PaymentTransactions)}
              />
              {account.registrationDateTime && canChangeUsername ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={changeUsernameInactive}
                  activeIcon={changeUsernameActive}
                  label={toolTypeToName(ToolType.ChangeUsername)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.ChangeUsername,
                  )}
                  onClick={() => onSelectTool(ToolType.ChangeUsername)}
                />
              ) : null}
              {canManageAutopay ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={autopayInactive}
                  activeIcon={autopayActive}
                  label={toolTypeToName(ToolType.ManageAutopay)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.ManageAutopay,
                  )}
                  onClick={() => onSelectTool(ToolType.ManageAutopay)}
                />
              ) : null}
              {account.registrationDateTime ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={notificationInactive}
                  activeIcon={notificationActive}
                  label={toolTypeToName(ToolType.TestPushNotification)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.TestPushNotification,
                  )}
                  onClick={() => onSelectTool(ToolType.TestPushNotification)}
                />
              ) : null}
              {canMakePayment ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={makePaymentInactive}
                  activeIcon={makePaymentActive}
                  label={toolTypeToName(ToolType.MakePayment)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.MakePayment,
                  )}
                  onClick={() => onSelectTool(ToolType.MakePayment)}
                />
              ) : null}
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={sourceAccountsInactive}
                activeIcon={sourceAccountsActive}
                label={toolTypeToName(ToolType.SourceAccounts)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.SourceAccounts,
                )}
                onClick={() => onSelectTool(ToolType.SourceAccounts)}
              />
              {import.meta.env.VITE_P2D_ENABLED === "true" && canP2D ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={permissionToDiscussInactive}
                  activeIcon={permissionToDiscussActive}
                  label={toolTypeToName(ToolType.PermissionToDiscuss)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.PermissionToDiscuss,
                  )}
                  onClick={() => onSelectTool(ToolType.PermissionToDiscuss)}
                />
              ) : null}
              {canC2D ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={cycleToDateTransactionsInactive}
                  activeIcon={cycleToDateTransactionsActive}
                  label={toolTypeToName(ToolType.CycleToDateTransactions)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.CycleToDateTransactions,
                  )}
                  onClick={() => onSelectTool(ToolType.CycleToDateTransactions)}
                />
              ) : null}
              {canViewStatementHistory ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={statementHistoryInactive}
                  activeIcon={statementHistoryActive}
                  label={toolTypeToName(ToolType.StatementHistory)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.StatementHistory,
                  )}
                  onClick={() => onSelectTool(ToolType.StatementHistory)}
                />
              ) : null}
              {canViewLostStolen ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={lostStolenInactive}
                  activeIcon={lostStolenActive}
                  label={toolTypeToName(ToolType.LostStolen)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.LostStolen,
                  )}
                  onClick={() => onSelectTool(ToolType.LostStolen)}
                />
              ) : null}
              {canViewCreditLineIncrease ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={creditLineIncreaseInactive}
                  activeIcon={creditLineIncreaseActive}
                  label={toolTypeToName(ToolType.CreditLineIncrease)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.CreditLineIncrease,
                  )}
                  onClick={() => onSelectTool(ToolType.CreditLineIncrease)}
                />
              ) : null}
              {canViewTerms ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={termsInactive}
                  activeIcon={termsActive}
                  label={toolTypeToName(ToolType.Terms)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.Terms,
                  )}
                  onClick={() => onSelectTool(ToolType.Terms)}
                />
              ) : null}
              {canViewPhones ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={phonesInactive}
                  activeIcon={phonesActive}
                  label={toolTypeToName(ToolType.Phones)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.Phones,
                  )}
                  onClick={() => onSelectTool(ToolType.Phones)}
                />
              ) : null}
              {canViewOnlineStatements ? (
                <SelectToolButton
                  className={styles.selectToolButton}
                  inactiveIcon={onlineStatementsInactive}
                  activeIcon={onlineStatementsActive}
                  label={toolTypeToName(ToolType.OnlineStatements)}
                  active={toolState.activeTools.some(
                    (tool) => tool.type === ToolType.OnlineStatements,
                  )}
                  onClick={() => onSelectTool(ToolType.OnlineStatements)}
                />
              ) : null}
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={paperlessServiceInactive}
                activeIcon={paperlessServiceActive}
                label={toolTypeToName(ToolType.PaperlessService)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.PaperlessService,
                )}
                onClick={() => onSelectTool(ToolType.PaperlessService)}
              />
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={cardActivationInactive}
                activeIcon={cardActivationActive}
                label={toolTypeToName(ToolType.CardActivation)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.CardActivation,
                )}
                onClick={() => onSelectTool(ToolType.CardActivation)}
              />
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={textBankingInactive}
                activeIcon={textBankingActive}
                label={toolTypeToName(ToolType.TextBanking)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.TextBanking,
                )}
                onClick={() => onSelectTool(ToolType.TextBanking)}
              />
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={resultsInactive}
                activeIcon={resultsActive}
                label={toolTypeToName(ToolType.Results)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.Results,
                )}
                onClick={() => onSelectTool(ToolType.Results)}
              />
              <SelectToolButton
                className={styles.selectToolButton}
                inactiveIcon={notesInactive}
                activeIcon={notesActive}
                label={toolTypeToName(ToolType.Notes)}
                active={toolState.activeTools.some(
                  (tool) => tool.type === ToolType.Notes,
                )}
                onClick={() => onSelectTool(ToolType.Notes)}
              />
              <ToolbarSummaryButton
                className={styles.toolbarSummaryButton}
                completedToolsCount={toolState.completedTools.length}
                disabled={!toolState.completedTools.length}
                onClick={() => {
                  scrollToCompleted();
                  onExpandOrCollapse();
                }}
              />
            </div>
          )}
        </div>
        <div className={styles.versionContainer}>
          v{import.meta.env.VITE_VERSION_CODE}
        </div>
      </div>
      {closeConfirmationTool && (
        <CloseToolConfirmationModal
          isOpen={!!closeConfirmationTool}
          toolNames={[toolTypeToName(closeConfirmationTool.type)]}
          onCloseTool={() => {
            toolState.onRequestCloseTool(closeConfirmationTool.id);
            setCloseConfirmationTool(undefined);
            onCloseToolbar();
          }}
          onRequestClose={() => setCloseConfirmationTool(undefined)}
        />
      )}
      <DebtDisclosureOnCloseWarningModal
        isOpen={showDebtDisclosureWarningModal}
        onRequestClose={() => setShowDebtDisclosureWarningModal(false)}
      />
      <WarningModal
        isOpen={showResultRequiredModal}
        modalLabel={strings.resultModalLabel(channel)}
        body={strings.resultMustComplete(channel)}
        closeText={strings.resultButtonText}
        onRequestClose={() => {
          setShowResultRequiredModal(false);
          toolState.onAddTool({
            type: ToolType.Results,
            inProgress: false,
          });
        }}
      />
    </div>
  );
}
